import React, { useEffect, useState } from "react";
import { supabase } from "../../shop/api/supabase";

const STATUSI = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const statusLabel = {
  pending: "Na čekanju", confirmed: "Potvrđeno",
  shipped: "Poslato", delivered: "Dostavljeno", cancelled: "Otkazano",
};

const statusColor = {
  pending: "#d4a84b", confirmed: "#2a7a2a",
  shipped: "#1a5a9a", delivered: "#5a3c1e", cancelled: "#a83228",
};

const EMAIL_STATUSI = ["confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrders({ focusOrderId, onFocusHandled }) {
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(null);
  const [modal, setModal]             = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [deliveryTime, setDeliveryTime]   = useState("");
  const [sending, setSending]         = useState(false);
  const [showPast, setShowPast]       = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  useEffect(() => {
    if (focusOrderId && orders.length > 0) {
      setExpanded(focusOrderId);
      onFocusHandled?.();
      setTimeout(() => {
        document.getElementById(`order-${focusOrderId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [focusOrderId, orders]);

  async function fetchOrders() {
    const { data, error } = await supabase
      .from("orders").select("*").order("created_at", { ascending: false });
    if (!error) setOrders(data);
    setLoading(false);
  }

  const handleStatusChange = (order, newStatus) => {
    if (EMAIL_STATUSI.includes(newStatus)) {
      setModal({ order, newStatus });
      setCustomMessage("");
      setDeliveryTime("");
    } else {
      applyStatus(order.id, newStatus);
    }
  };

  async function applyStatus(orderId, status) {
    await supabase.from("orders").update({ status }).eq("id", orderId);
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status } : o));
  }

  async function confirmModal(sendEmail) {
    setSending(true);
    const { order, newStatus } = modal;
    await applyStatus(order.id, newStatus);
    if (sendEmail) {
      await supabase.functions.invoke("send-status-email", {
        body: { order, status: newStatus, customMessage, deliveryTime },
      });
    }
    setSending(false);
    setModal(null);
    setCustomMessage("");
    setDeliveryTime("");
  }

  if (loading) return <div className="admin-loading">Učitavanje...</div>;

  const today = new Date().toISOString().split("T")[0];
  const activeStatuses = ["pending", "confirmed", "shipped"];
  const activeOrders = orders.filter((o) =>
    activeStatuses.includes(o.status) && (!o.delivery_date || o.delivery_date >= today)
  );
  const pastOrders = orders.filter((o) =>
    !activeStatuses.includes(o.status) || (o.delivery_date && o.delivery_date < today)
  );

  const renderOrder = (order) => (
    <div key={order.id} id={`order-${order.id}`} className={`admin-order${order.id === focusOrderId ? " admin-order--focused" : ""}`}>
      <div className="admin-order__header" onClick={() => setExpanded(expanded === order.id ? null : order.id)}>
        <div className="admin-order__meta">
          <span className="admin-order__id">#{order.id.slice(0, 8)}</span>
          <span className="admin-order__name">{order.customer?.ime}</span>
          <span className="admin-order__email">{order.customer?.email}</span>
          <span className="admin-order__date">
            {new Date(order.created_at).toLocaleDateString("sr-Latn-RS")}
          </span>
          {order.delivery_date && (
            <span style={{ fontSize: "0.78rem", color: "#1a5a9a" }}>
              🚚 {new Date(order.delivery_date).toLocaleDateString("sr-Latn-RS", { day: "numeric", month: "short" })}
              {order.delivery_hour !== null ? ` · ${String(order.delivery_hour).padStart(2, "0")}:00h` : ""}
            </span>
          )}
          <strong className="admin-order__total">
            {Number(order.total).toLocaleString("sr-RS")} RSD
          </strong>
        </div>
        <div className="admin-order__controls">
          <select
            value={order.status}
            onChange={(e) => { e.stopPropagation(); handleStatusChange(order, e.target.value); }}
            className="admin-order__status"
            style={{ borderColor: statusColor[order.status] }}
            onClick={(e) => e.stopPropagation()}
          >
            {STATUSI.map((s) => <option key={s} value={s}>{statusLabel[s]}</option>)}
          </select>
          <span className="admin-order__toggle">{expanded === order.id ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded === order.id && (
        <div className="admin-order__body">
          <div className="admin-order__cols">
            <div>
              <h4>Kupac</h4>
              <p>{order.customer?.ime}</p>
              <p>{order.customer?.email}</p>
              <p>{order.customer?.telefon}</p>
              <p>{order.customer?.adresa}</p>
              {order.customer?.napomena && <p><em>Napomena: {order.customer.napomena}</em></p>}
            </div>
            <div>
              <h4>Stavke</h4>
              {order.items?.map((item, i) => (
                <div key={i} className="admin-order__item">
                  <span>{item.title}</span>
                  <span>× {item.kolicina}</span>
                  <span>{item.cena}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-orders">
      <h2 className="admin-section-title">Porudžbine ({activeOrders.length})</h2>

      {activeOrders.length === 0 ? (
        <p className="admin-empty">Nema aktivnih porudžbina.</p>
      ) : (
        <div className="admin-orders__list">
          {activeOrders.map(renderOrder)}
        </div>
      )}

      {pastOrders.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <button
            onClick={() => setShowPast((v) => !v)}
            style={{ display: "flex", alignItems: "center", gap: "0.6rem", background: "none", border: "none", cursor: "pointer", padding: "0.5rem 0", width: "100%" }}
          >
            <span style={{ fontFamily: "Cinzel, serif", fontSize: "0.9rem", color: "#8a7060", fontWeight: 600 }}>
              Protekle porudžbine ({pastOrders.length})
            </span>
            <span style={{ fontSize: "0.72rem", color: "#a09080" }}>{showPast ? "▲ sakrij" : "▼ prikaži"}</span>
            <span style={{ flex: 1, height: 1, background: "#e8d4b0", marginLeft: "0.3rem" }} />
          </button>

          {showPast && (
            <div className="admin-orders__list" style={{ marginTop: "0.5rem", opacity: 0.7 }}>
              {pastOrders.map(renderOrder)}
            </div>
          )}
        </div>
      )}

      {/* MODAL */}
      {modal && (
        <div className="admin-modal__backdrop" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">
              Promeni status → <span style={{ color: statusColor[modal.newStatus] }}>{statusLabel[modal.newStatus]}</span>
            </h3>
            <p className="admin-modal__sub">
              Kupac: <strong>{modal.order.customer?.ime}</strong> ({modal.order.customer?.email})
            </p>

            {modal.newStatus === "shipped" && (
              <div className="admin-modal__field">
                <label>Okvirno vreme dostave (opciono)</label>
                <input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} placeholder="npr. 15:00" />
              </div>
            )}

            <div className="admin-modal__field">
              <label>Dodatna poruka kupcu (opciono)</label>
              <textarea value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows={3} placeholder="npr. Broj pošiljke: 123456789..." />
            </div>

            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--primary" onClick={() => confirmModal(true)} disabled={sending}>
                {sending ? "Slanje..." : "Promeni status i pošalji email"}
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => confirmModal(false)} disabled={sending}>
                Samo promeni status
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => setModal(null)} disabled={sending}>
                Otkaži
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
